import { Suspense } from 'react'

import { createFileRoute } from '@tanstack/react-router'

import { RouteErrorComponent } from '@/features/app-shell/route-error-component'
import { LogSourceDetailSkeleton } from '@/features/integrations/collectors/components/skeletons'
import { collectorQueries } from '@/features/integrations/collectors/queries'
import { lazyNamed } from '@/utils/lazy-named'

import { getCurrentTenant } from '@/shared/auth/store'

import { ErrorBoundary } from '@/components/common/error-boundary'

const LogSourceDetail = lazyNamed(
	() =>
		import('@/features/integrations/collectors/components/log-source-detail'),
	'LogSourceDetail',
)

export const Route = createFileRoute(
	'/_dashboard/integrations/collectors/$collectorId/$sourceId',
)({
	loader: ({ params, context: { queryClient } }) => {
		const tenant = getCurrentTenant()

		// Prefetch what the section suspends on, so arriving from the Collector's
		// sources table renders a warm cache instead of waterfalling (A-07). The
		// source is read out of its Collector's detail entry — the one the
		// sources table was just rendered from — so there is no source to fetch.
		if (tenant) {
			queryClient.prefetchQuery(collectorQueries.logSourceOptions(tenant))
			queryClient.prefetchQuery(
				collectorQueries.detail(tenant, params.collectorId),
			)
		}
	},

	// Static, because the source's name is not known until the query resolves —
	// and a title that arrived one render late would be worse than a stable one.
	head: () => ({
		meta: [{ title: 'Log source | Integrations | BitLyft AIR®' }],
	}),

	component: LogSourceDetailPage,
	pendingComponent: LogSourceDetailSkeleton,
	errorComponent: RouteErrorComponent,
})

function LogSourceDetailPage() {
	const { collectorId, sourceId } = Route.useParams()

	return (
		<ErrorBoundary
			feature="integrations"
			section="log-source-detail"
			entityId={sourceId}
		>
			<Suspense fallback={<LogSourceDetailSkeleton />}>
				<LogSourceDetail collectorId={collectorId} sourceId={sourceId} />
			</Suspense>
		</ErrorBoundary>
	)
}
